import { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import RetailerAuth from '../components/retailer/RetailerAuth';
import RetailerDashboard from '../components/retailer/RetailerDashboard';
import RetailerPostItem from '../components/retailer/RetailerPostItem';
import RetailerInventory from '../components/retailer/RetailerInventory';
import RetailerOrders from '../components/retailer/RetailerOrders';
import RetailerAnalytics from '../components/retailer/RetailerAnalytics';
import RetailerSettingsHub from '../components/retailer/RetailerSettingsHub';
import RetailerStoreInfo from '../components/retailer/RetailerStoreInfo';
import RetailerPickupDelivery from '../components/retailer/RetailerPickupDelivery';
import RetailerPayout from '../components/retailer/RetailerPayout';
import RetailerNotifications from '../components/retailer/RetailerNotifications';
import RetailerSecurity from '../components/retailer/RetailerSecurity';
import RetailerCorporateDashboard from '../components/retailer/RetailerCorporateDashboard';
import ForcePasswordChange from '../components/retailer/ForcePasswordChange';
import RetailerWelcomeLink from '../components/retailer/RetailerWelcomeLink';
import RetailerLayout from '../components/retailer/RetailerLayout';
import StoreCodeRegistry from '../components/retailer/StoreCodeRegistry';
import RetailerOnboardingWizard from '../components/retailer/RetailerOnboardingWizard';
import RetailerOnboardingGuide from '../components/retailer/RetailerOnboardingGuide';
// Barcode-First Components (Phase 2)
import { BarcodeScanner } from '../components/retailer/barcode';
// Dashboard Components
import ScanHistoryPage from '../components/retailer/dashboard/ScanHistoryPage';
// Help Center
import HelpPage from '../pages/HelpPage';
import RetailerCopilot from '../components/retailer/RetailerCopilot';
import RetailerSettlementDashboard from '../components/retailer/RetailerSettlementDashboard';
import RetailerReconciliation from '../components/retailer/RetailerReconciliation';
import RetailerWebhookConfig from '../components/retailer/RetailerWebhookConfig';
import RetailerImpactDashboard from '../components/retailer/RetailerImpactDashboard';
import BuildInfo from '../components/BuildInfo';
import Footer from '../components/legal/Footer';
import { auth } from '../utils/api';

export default function RetailerApp() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    
    if (token && savedUser) {
      const parsedUser = JSON.parse(savedUser);
      if (parsedUser.role === 'DRLP') {
        setUser(parsedUser);
      } else {
        localStorage.clear();
      }
    }
    setLoading(false);
  }, []);

  const handleLogin = (token, userData) => {
    if (userData.role !== 'DRLP') {
      throw new Error('Invalid user role for Retailer App');
    }
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    window.dispatchEvent(new Event('auth-changed'));
    setUser(userData);
    navigate('/retailer/dashboard');
  };

  const handleLogout = () => {
    localStorage.clear();
    window.dispatchEvent(new Event('auth-changed'));
    setUser(null);
    navigate('/retailer');
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen">Loading...</div>;
  }

  // Allow help page without authentication
  if (location.pathname === '/retailer/help') {
    return (
      <>
        <HelpPage />
        <BuildInfo />
      </>
    );
  }

  // Magic-link welcome route: bypasses the manual login form so Chrome
  // never sees a credential-bearing <form> POST for the disposable temp pw.
  if (!user && location.pathname === '/retailer/welcome') {
    return (
      <>
        <RetailerWelcomeLink onLogin={handleLogin} />
        <BuildInfo />
      </>
    );
  }

  if (!user) {
    return (
      <>
        <RetailerAuth onLogin={handleLogin} />
        <BuildInfo />
      </>
    );
  }

  // Forced password reset: if the user was issued a temporary password
  // (must_reset_password=true on the user doc — set by location-invite or
  // team-invite flows), block every retailer route until they pick a new one.
  if (user.must_reset_password) {
    const refreshUser = async () => {
      try {
        const res = await auth.me();
        const fresh = res.data;
        localStorage.setItem('user', JSON.stringify(fresh));
        setUser(fresh);
        navigate('/retailer/dashboard');
      } catch {
        // If /auth/me fails after a successful password change, fall back to
        // a hard logout so the user doesn't end up in a broken half-state.
        handleLogout();
      }
    };
    return (
      <>
        <ForcePasswordChange user={user} onSuccess={refreshUser} onLogout={handleLogout} />
        <BuildInfo />
      </>
    );
  }

  return (
    <>
      <Routes>
        <Route path="/" element={<Navigate to="/retailer/dashboard" replace />} />
        <Route path="/dashboard" element={<RetailerDashboard user={user} onLogout={handleLogout} />} />
        <Route path="/onboarding" element={<RetailerOnboardingWizard />} />
        <Route path="/scan" element={<BarcodeScanner user={user} onLogout={handleLogout} />} />
        <Route path="/post-item" element={<RetailerPostItem user={user} onLogout={handleLogout} />} />
        <Route path="/inventory" element={<RetailerInventory user={user} onLogout={handleLogout} />} />
        <Route path="/orders" element={<RetailerOrders user={user} onLogout={handleLogout} />} />
        <Route path="/settlements" element={<RetailerSettlementDashboard user={user} onLogout={handleLogout} />} />
        <Route path="/reconciliation" element={<RetailerReconciliation user={user} onLogout={handleLogout} />} />
        <Route path="/webhooks" element={<RetailerWebhookConfig user={user} onLogout={handleLogout} />} />
        <Route path="/impact" element={
          <RetailerLayout user={user} onLogout={handleLogout}>
            <RetailerImpactDashboard />
          </RetailerLayout>
        } />
        <Route path="/analytics" element={
          <RetailerLayout user={user} onLogout={handleLogout}>
            <RetailerAnalytics />
          </RetailerLayout>
        } />
        <Route path="/settings" element={<RetailerSettingsHub user={user} onLogout={handleLogout} />} />
        <Route path="/settings/store-info" element={<RetailerStoreInfo user={user} onLogout={handleLogout} />} />
        <Route path="/settings/pickup-delivery" element={<RetailerPickupDelivery user={user} onLogout={handleLogout} />} />
        <Route path="/settings/payout" element={<RetailerPayout user={user} onLogout={handleLogout} />} />
        <Route path="/settings/notifications" element={<RetailerNotifications user={user} onLogout={handleLogout} />} />
        <Route path="/settings/security" element={<RetailerSecurity user={user} onLogout={handleLogout} />} />
        <Route path="/corporate" element={<RetailerCorporateDashboard user={user} onLogout={handleLogout} />} />
        <Route path="/store-codes" element={<StoreCodeRegistry user={user} onLogout={handleLogout} />} />
        <Route path="/history" element={<ScanHistoryPage user={user} onLogout={handleLogout} />} />
        <Route path="/help" element={<HelpPage />} />
        <Route path="/guide" element={<RetailerOnboardingGuide />} />
      </Routes>
      <RetailerCopilot />
      <Footer variant="retailer" />
      <BuildInfo />
    </>
  );
}
